import {CampaignTreeType} from 'common/src/modules/campaigns/campaigns.types';
import {addCard} from 'common/src/modules/campaigns/store/campaignLogicTab/actions/addCard';
import {editCard} from 'common/src/modules/campaigns/store/campaignLogicTab/actions/editCard';
import {
  ContentFeedNode,
  DTCompoundConditionNode,
  DTConditionNode,
  DTCreativeNode,
  DTDistributionNode,
  DTNativeCreativeNode,
  DTOptimizationNode,
  DTProductSelectionNode,
  DTScheduleNode,
  DTSequenceNode,
  DTStoreNode,
  DTWeightedNode,
} from 'common/src/modules/decisionTree/decisionTree.types';
import {MapNode, NodeOutputData, NodeType} from 'common/src/modules/nodes/nodes.types';
import {CLStore} from 'common/src/redux/store';
import refreshCampaignIssues from './refreshCampaignIssues';

// Type for node data that can be passed to addOrUpdateNodeState
// This represents the minimum required properties plus any card-specific properties
export type NodeData = {
  id: number;
  type: NodeType;
  name?: string;
  description?: string | null;
  outputs?: NodeOutputData[];
};

export enum AddOrUpdateNodeStateType {
  Add = 'add',
  Update = 'update',
}

/**
 * Add or update a node in the campaign logic.
 * This function is used to add or update a node in the campaign logic.
 * @param node - The node data to add or update
 * @param type - The type of operation to perform
 */
export default async function addOrUpdateNodeState<
  T extends
    | DTCreativeNode
    | DTNativeCreativeNode
    | ContentFeedNode
    | DTOptimizationNode
    | DTScheduleNode
    | DTWeightedNode
    | DTConditionNode
    | DTCompoundConditionNode
    | DTStoreNode
    | DTProductSelectionNode
    | DTDistributionNode
    | DTSequenceNode,
>(
  store: CLStore,
  node: T,
  type: AddOrUpdateNodeStateType = AddOrUpdateNodeStateType.Add,
): Promise<number> {
  const nextNodeId = store.getState().campaignPage.campaignLogic.nextNodeId;

  const updatedNode = {
    ...node,
    id: node.id > 1 ? node.id : nextNodeId,
  } as T;

  const cardType = updatedNode.type;

  // Create the MapNode structure expected by the addCard thunk
  const newNode: MapNode = {
    id: updatedNode.id,
    type: cardType,
    inputs: [],
    outputs: [],
    nextOutputId: 1,
  };

  // Add any card-type-specific properties to the node
  // Note: This might need expansion based on actual backend requirements
  const nodeWithSpecificProps = {
    ...newNode,
    ...updatedNode,
  };

  if (type === AddOrUpdateNodeStateType.Update) {
    const response = await store.dispatch(
      editCard({
        updatedNode: nodeWithSpecificProps as MapNode,
        campaignTreeType: CampaignTreeType.Campaign,
      }),
    );

    if (response.meta.requestStatus === 'rejected') {
      const errorMessage =
        (response as {error?: {message?: string}}).error?.message || 'Failed to update card';
      throw new Error(errorMessage);
    }
  } else {
    // Dispatch the addCard thunk
    const response = await store.dispatch(
      addCard({
        newNode: nodeWithSpecificProps as MapNode,
        campaignTreeType: CampaignTreeType.Campaign, // Default to Campaign tree type
      }),
    );

    if (response.meta.requestStatus === 'rejected') {
      const errorMessage =
        (response as {error?: {message?: string}}).error?.message || 'Failed to add card';
      throw new Error(errorMessage);
    }
  }

  // Refresh campaign issues after successful add/update
  await refreshCampaignIssues(store);

  return updatedNode.id;
}
